import React, { useEffect, useState } from "react";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { useFormikContext } from "formik";

import GoogleDocsSVG from "../../../../assets/svg/google-docs.svg";
import ShopifySVG from "../../../../assets/svg/shopify.svg";
import WordpressSVG from "../../../../assets/svg/wordpress.svg";
import { generatePath } from "react-router-dom";
import {
  ROUTE_DESTINATIONS,
  ROUTE_DESTINATIONS_EDIT,
} from "../../../../constants/routes";
import {
  DestinationTabs,
  DestinationTypeEnum,
  DestinationTypeMap,
} from "../../../../enums/destination";
import useSelectedAccount from "../../../../hooks/useSelectedAccount";
import { Template } from "../../../../models";
import {
  AllDestinations,
  GoogleDocsDestination,
} from "../../../../models/destination";
import { useGetDestinationsQuery } from "../../../../redux/services/destinations";

import RefreshButton from "../../../../components/common/RefreshButton";
import Button from "../../../../components/form/Button";
import SelectV2, { OptionType } from "../../../../components/form/CustomSelect";
import TemplateContainer from "../common/TemplateContainer";

const getDestinationIcon = (type: DestinationTypeEnum) => {
  switch (type) {
    case DestinationTypeEnum.GOOGLE_DOCS:
      return GoogleDocsSVG;
    case DestinationTypeEnum.SHOPIFY:
      return ShopifySVG;
    default:
      return WordpressSVG;
  }
};

// a Google Docs destination only saves articles once a Google account is connected to it
const getGoogleProblem = (
  destination?: AllDestinations
): "not-connected" | "reconnect" | null => {
  if (destination?.destinationTypeId !== DestinationTypeEnum.GOOGLE_DOCS) {
    return null;
  }
  const { googleEmail, needsReconnect } = (destination as GoogleDocsDestination)
    .destination;
  if (needsReconnect) return "reconnect";
  return googleEmail ? null : "not-connected";
};

const DestinationSelector = () => {
  const { values, setFieldValue } = useFormikContext<Partial<Template>>();
  const { accountId } = useSelectedAccount();

  const [destinations, setDestinations] = useState<OptionType[]>([]);

  // Fetch data with pagination
  const { data, refetch, isFetching } = useGetDestinationsQuery(
    {
      accountId,
      offset: 0,
      limit: 25,
      includeTotalCount: true,
    },
    { skip: !accountId }
  );

  useEffect(() => {
    if (data?.data) {
      const newDestinations = data.data.map((destination: AllDestinations) => {
        const problem = getGoogleProblem(destination);
        return {
          value: destination.destinationId,
          label:
            destination.name +
            (problem === "reconnect"
              ? " (reconnect needed)"
              : problem === "not-connected"
                ? " (not connected)"
                : ""),
          icon: getDestinationIcon(destination.destinationTypeId),
        };
      });

      setDestinations([...newDestinations]);
    }
  }, [data]);

  const selectedDestination: AllDestinations | undefined = data?.data?.find(
    (destination: AllDestinations) =>
      destination.destinationId === values?.destinationId
  );
  const googleProblem = getGoogleProblem(selectedDestination);

  return (
    <div className="animate-fadeIn">
      <TemplateContainer className="flex flex-col gap-4">
        <div className="flex gap-2 items-center">
          <SelectV2
            label="Destination"
            placeholder="-"
            options={destinations || []}
            selected={destinations.find(
              (option) => option.value === values?.destinationId
            )}
            setSelected={(option) => {
              setFieldValue("destinationId", option?.value ?? null);
            }}
            className="w-full"
          />
          <RefreshButton
            className="mt-6"
            refetch={refetch}
            loading={isFetching}
            tooltip="Refresh destination list."
          />
        </div>
        {googleProblem && selectedDestination && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            {googleProblem === "reconnect"
              ? "Google access for this destination was revoked, so articles are not being saved to Google Docs."
              : "This Google Docs destination is not connected to a Google account yet, so articles will not be saved until it is."}{" "}
            <a
              className="underline"
              target="_blank"
              rel="noreferrer"
              href={
                generatePath(ROUTE_DESTINATIONS_EDIT, {
                  id: selectedDestination.destinationId!,
                }) +
                `?type=${DestinationTypeMap[DestinationTypeEnum.GOOGLE_DOCS]}`
              }
            >
              {googleProblem === "reconnect" ? "Reconnect it" : "Connect it"}
            </a>
          </div>
        )}
        <Button
          paddingZero
          variant="transparent-primary"
          className="flex items-center gap-1 w-max"
          onClick={() => {
            window.open(
              ROUTE_DESTINATIONS + `?tab=${DestinationTabs.ALL}`,
              "_blank"
            );
          }}
        >
          Add new destination <ArrowTopRightOnSquareIcon className="size-4" />
        </Button>
      </TemplateContainer>
    </div>
  );
};

export default DestinationSelector;
